import React from 'react';
import { Link } from 'react-router-dom';
import usePageMeta from '../hooks/usePageMeta';
import './Legal.css';

/* Two legal-boilerplate pages — Privacy Policy and Terms of Service.
 * Both share the same scrolling-doc layout so they live in one file.
 * Wired to /privacy and /terms routes via App.js.
 *
 * The copy here is intentionally generic but accurate for what
 * Heaven Sent actually does: a small-business website that takes
 * quote requests via a form, stores them in a Postgres DB, and
 * sends them to QuickBooks. No e-commerce checkout, no tracking
 * cookies beyond the analytics provider chosen at deploy time.
 *
 * Have a real attorney review before going live in any high-stakes
 * jurisdiction.
 */

const SITE = 'Heaven Sent Promotions & Apparel';
const DOMAIN = 'heavensentpromos.com';
const ADDRESS = '900 Basse Rd, San Antonio, TX 78212';
const PHONE = '(210) 662-7958';
const EMAIL = 'susan@heavensentpromos.com';
const EFFECTIVE = 'June 2026';

export function Privacy() {
  usePageMeta({
    title: 'Privacy Policy',
    description: 'How Heaven Sent Promotions collects, uses, and protects information you share with us through quote requests and contact forms.',
    path: '/privacy',
  });
  return (
    <main className="legal-page">
      <header className="legal-hero">
        <p className="section-label">Legal</p>
        <h1 className="script-font">Privacy Policy</h1>
        <p className="legal-effective">Effective {EFFECTIVE}</p>
      </header>

      <article className="legal-content">
        <p>
          {SITE} ("we", "our", "us") respects your privacy. This Privacy Policy
          explains what information we collect when you visit {DOMAIN} or
          submit a quote or contact request, how we use it, and the choices
          you have. By using this site you agree to the practices described
          here.
        </p>

        <h2>What we collect</h2>
        <p>
          We only collect information you give us directly. That includes:
        </p>
        <ul>
          <li><strong>Contact information</strong> you provide on a quote or contact form (name, email address, phone number, company name).</li>
          <li><strong>Order details</strong> you provide on a quote (the products, colors, sizes, quantities, deadlines, and design files you upload).</li>
          <li><strong>Basic technical information</strong> automatically logged by our hosting provider, such as your IP address, browser, and pages visited. This is used for security and to improve the site.</li>
        </ul>
        <p>
          We do not sell or rent any of this information.
        </p>

        <h2>How we use it</h2>
        <ul>
          <li>To prepare and deliver the quotes and orders you ask us for.</li>
          <li>To contact you about your order or follow up on a request you submitted.</li>
          <li>To create an Estimate or Invoice in our QuickBooks accounting system.</li>
          <li>To improve our website, products, and services.</li>
          <li>To comply with our legal obligations.</li>
        </ul>

        <h2>How we share it</h2>
        <p>
          We share your information only with service providers who help us run
          our business — for example, our cloud-hosting provider, our payment
          processor, and Intuit QuickBooks (for invoicing). These providers
          access your information only as needed to perform their service and
          are required to keep it confidential. We may also disclose
          information when required by law.
        </p>

        <h2>QuickBooks Online</h2>
        <p>
          When we prepare a quote or order, we may create a matching customer
          record and Estimate in our QuickBooks Online account, which is
          operated by Intuit Inc. The information we send to QuickBooks is
          limited to what's needed to bill you: your name, email address,
          phone number, company name, and the items, quantities, and pricing on
          your order. Intuit processes this information under its own{' '}
          <a href="https://www.intuit.com/privacy/" target="_blank" rel="noopener noreferrer">privacy policy</a>.
          We do not retrieve your personal data back out of QuickBooks for any
          other purpose.
        </p>

        <h2>How long we keep it</h2>
        <p>
          Quote and order records are retained for as long as needed to
          complete the work and meet our recordkeeping obligations. Submitted
          quote records that don't progress into orders are automatically
          purged from our active database after thirty (30) days.
        </p>

        <h2>Cookies and analytics</h2>
        <p>
          We use a privacy-respecting analytics service to understand which
          pages visitors find useful. The analytics service does not place
          tracking cookies, does not collect personally identifying
          information, and aggregates data across all visitors. You can
          disable JavaScript in your browser if you'd like to opt out
          entirely.
        </p>

        <h2>Your choices</h2>
        <p>
          You can ask us to view, correct, or delete the personal information
          you've shared with us by emailing <a href={`mailto:${EMAIL}`}>{EMAIL}</a> or
          calling {PHONE}. We respond to verified requests within thirty (30)
          days.
        </p>

        <h2>Children</h2>
        <p>
          This site is not directed to children under 13, and we don't
          knowingly collect personal information from children. If you
          believe a child has provided us information, contact us and we'll
          delete it.
        </p>

        <h2>Changes to this policy</h2>
        <p>
          We may update this Privacy Policy from time to time. Updates are
          posted on this page with a new effective date.
        </p>

        <h2>Contact us</h2>
        <p>
          Questions about this policy?
          <br />
          {SITE}<br />
          {ADDRESS}<br />
          {PHONE} · <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
        </p>

        <p className="legal-footer-link">
          <Link to="/terms">Read our Terms of Service →</Link>
        </p>
      </article>
    </main>
  );
}

export function Terms() {
  usePageMeta({
    title: 'Terms of Service',
    description: 'Terms of service for Heaven Sent Promotions: how quotes, orders, payments, and intellectual property are handled.',
    path: '/terms',
  });
  return (
    <main className="legal-page">
      <header className="legal-hero">
        <p className="section-label">Legal</p>
        <h1 className="script-font">Terms of Service</h1>
        <p className="legal-effective">Effective {EFFECTIVE}</p>
      </header>

      <article className="legal-content">
        <p>
          These Terms govern your use of {DOMAIN} and any orders you
          place with {SITE} ("we", "our", "us"). By using the site or
          submitting a quote you agree to these Terms.
        </p>

        <h2>Quotes and estimates</h2>
        <p>
          Pricing shown on this site is an estimate only. We confirm
          the final price after reviewing your design and order
          details. Quotes are valid for thirty (30) days from the date
          we send them unless we say otherwise in writing.
        </p>

        <h2>Orders and payment</h2>
        <p>
          An order is final once we send a written confirmation and
          receive any deposit we've requested. Standard orders require
          full payment before shipping. A 3% convenience fee applies
          to credit card transactions to cover processor costs.
        </p>

        <h2>Production and turnaround</h2>
        <p>
          Standard turnaround is approximately seven to ten business
          days from the date your final design is approved, depending
          on the size of the order and the products selected. Rush
          turnaround may be available for an additional fee. Delivery
          dates we communicate are estimates and are not guaranteed
          unless we explicitly state otherwise in writing.
        </p>

        <h2>Cancellations and changes</h2>
        <p>
          You can change or cancel your order before production
          begins. Once production begins, changes may not be
          possible. If your order is cancelled after production has
          started, you are responsible for materials and labor used
          to date.
        </p>

        <h2>Returns</h2>
        <p>
          Because every order is custom-made for you, we do not
          accept returns or exchanges except in the case of a defect
          attributable to us. If you receive a defective item, contact
          us within seven (7) days of delivery and we'll make it
          right — usually by reproducing the item or refunding the
          affected portion of your order.
        </p>

        <h2>Artwork and intellectual property</h2>
        <p>
          You confirm that any artwork, logos, or other content you
          upload to us is your own or that you have permission to use
          it. We're not responsible for verifying ownership.
          You retain all rights to your own artwork. Any artwork we
          create on your behalf becomes yours upon final payment.
          Don't reproduce or copy work we've designed without our
          permission.
        </p>

        <h2>Limitation of liability</h2>
        <p>
          To the maximum extent permitted by law, our total liability
          for any claim arising out of or relating to these Terms or
          our products is limited to the amount you paid for the
          order in question. We are not liable for indirect,
          incidental, or consequential damages.
        </p>

        <h2>Governing law</h2>
        <p>
          These Terms are governed by the laws of the State of Texas.
          Any dispute will be brought in the state or federal courts
          located in Bexar County, Texas.
        </p>

        <h2>Changes to these Terms</h2>
        <p>
          We may update these Terms from time to time. Updates are
          posted on this page with a new effective date. Continued
          use of the site after a change is your agreement to the
          updated Terms.
        </p>

        <h2>Contact us</h2>
        <p>
          {SITE}<br />
          {ADDRESS}<br />
          {PHONE} · <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
        </p>

        <p className="legal-footer-link">
          <Link to="/privacy">Read our Privacy Policy →</Link>
        </p>
      </article>
    </main>
  );
}
